import { Icon, ProfilePicture, Text } from "@components/library";
import { AdminTag, LicenseTag, SuperAdminTag } from "@components/library/Tags/users";
import { COLORS, FONTS, STYLES, WINDOW_DIMENSIONS } from "@constants";
import { setShowErrorBoundary } from "@redux/actions/defaultActions";
import { RootState } from "@redux/store";
import { signOut } from "@requests/users";
import { account_settings_path, company_path, profile_page_path } from "@routes/routes";
import { marketplacePath } from "@routes/utils";
import Tippy from "@tippyjs/react";
import { SegmentEventName } from "@tsTypes/__generated__/enums";
import { UserRole } from "@tsTypes/users";
import { track } from "@utils/appUtils";
import { userShortName } from "@utils/userUtils";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import styled from "styled-components";
import useNavbarLinks from "./useNavbarLinks";

// SSO endpoint for logged-in users - redirects through our backend for JWT authentication
const KNOWLEDGE_CENTER_SSO_URL = "/knowledge-base/sso";
// Direct URL for non-authenticated contexts
// const KNOWLEDGE_CENTER_URL = "https://knowledge.halo.science/";

const NavbarDropdownMenu = () => {
  const currentUser = useSelector((state: RootState) => state.profiles.currentUser);
  const isTablet = useSelector((state: RootState) => state.window.isTablet);
  const showErrorBoundary = useSelector(
    (state: RootState) => state.defaultReducer.showErrorBoundary
  );

  const dispatch = useDispatch();
  const navbarLinks = useNavbarLinks();

  const [isVisible, setIsVisible] = useState(false);

  const handleLogout = async () => {
    await signOut();
    window.location.href = marketplacePath();
  };

  const handleHelpCenter = () => {
    track(SegmentEventName.CLICK, {
      ui_component: "Navbar Help Center",
    });
  };

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>, to: string) => {
    if (showErrorBoundary) {
      e.preventDefault();
      dispatch(setShowErrorBoundary(false));
      window.location.href = to;
    }
    setIsVisible(false);
  };

  const userName = userShortName(currentUser);
  const userTitle =
    currentUser.role === UserRole.SPONSOR
      ? currentUser.profile_info.company_name
      : currentUser.profile_info.title;

  return (
    <Tippy
      visible={isVisible}
      onClickOutside={() => setIsVisible(false)}
      placement={isTablet ? "bottom-start" : "bottom-end"}
      interactive
      hideOnClick={false}
      offset={[0, 4]}
      render={(attrs) =>
        isVisible && (
          <DropdownContainer
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...attrs}
            onMouseEnter={() => setIsVisible(true)}
            onMouseLeave={() => setIsVisible(false)}
          >
            <HeaderSection>
              <ProfilePicture user={currentUser} size="lg" />
              <UserInfoContainer>
                <NameContainer>
                  <Text font={FONTS.MEDIUM_1} color={COLORS.HALO_BLUE} as="span">
                    {userName}
                  </Text>
                  {currentUser.role === UserRole.SPONSOR &&
                    currentUser.profile_info?.is_super_admin && (
                      <SuperAdminTagContainer>
                        <SuperAdminTag />
                      </SuperAdminTagContainer>
                    )}
                </NameContainer>
                <Text font={FONTS.REGULAR_2} color={COLORS.NEUTRAL_800} as="span">
                  {userTitle || ""}
                </Text>
                {currentUser.role === UserRole.SPONSOR && (
                  <ProfileTags>
                    {Boolean(currentUser.profile_info?.license) && (
                      <LicenseTag type={currentUser.profile_info?.license} />
                    )}
                    {currentUser.profile_info?.is_admin && <AdminTag />}
                  </ProfileTags>
                )}
              </UserInfoContainer>
            </HeaderSection>
            <MenuSection>
              {isTablet &&
                currentUser.id &&
                navbarLinks.map((link) => (
                  <MenuItem
                    key={link.to}
                    as={Link}
                    to={link.to}
                    onClick={(e) => handleLinkClick(e, link.to)}
                    data-testid={link["data-testid"]}
                  >
                    {link.label}
                  </MenuItem>
                ))}
              {currentUser.role === UserRole.SCIENTIST && (
                <MenuItem
                  as={Link}
                  to={profile_page_path(currentUser.profile_id)}
                  onClick={(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) =>
                    handleLinkClick(e, profile_page_path(currentUser.profile_id))
                  }
                  data-testid="navbar-menu-edit-profile"
                >
                  Edit profile
                </MenuItem>
              )}
              {currentUser.role === UserRole.SPONSOR &&
                currentUser.profile_info.company_identifier && (
                  <MenuItem
                    as={Link}
                    to={company_path(currentUser.profile_info.company_identifier)}
                    onClick={(e) =>
                      handleLinkClick(e, company_path(currentUser.profile_info.company_identifier))
                    }
                  >
                    {currentUser.profile_info.is_admin ? "Edit" : "View"} Company Page
                  </MenuItem>
                )}
              <MenuItem
                as={Link}
                to={account_settings_path()}
                onClick={(e) => handleLinkClick(e, account_settings_path())}
              >
                Account settings
              </MenuItem>
              <MenuItem as="a" href={KNOWLEDGE_CENTER_SSO_URL} onClick={handleHelpCenter}>
                Help center
              </MenuItem>
              <MenuItem as="button" onClick={handleLogout}>
                Sign out
              </MenuItem>
            </MenuSection>
          </DropdownContainer>
        )
      }
    >
      <TriggerButton
        type="button"
        onClick={() => setIsVisible((prev) => !prev)}
        data-testid="navbar-menu-trigger"
      >
        <ProfilePictureWrapper data-testid="navbar-menu-profile-pic">
          <ProfilePicture user={currentUser} size="sm" margin="0 5px 0 0" />
        </ProfilePictureWrapper>
        <IconWrapper>
          <Icon name="Chevron Down" color={COLORS.NEUTRAL_400} />
        </IconWrapper>
        {isTablet && <Icon name="Burger" size="lg" />}
      </TriggerButton>
    </Tippy>
  );
};

export default NavbarDropdownMenu;

const TriggerButton = styled.button`
  display: flex;
  align-items: center;
  margin-left: 24px;
  border: none;
  background: none;
  outline: none;
  cursor: pointer;

  ${WINDOW_DIMENSIONS.TABLET_MEDIA_QUERY} {
    margin-left: 0;
  }
`;

const ProfilePictureWrapper = styled.div`
  ${WINDOW_DIMENSIONS.TABLET_MEDIA_QUERY} {
    display: none;
  }
`;

const IconWrapper = styled.div`
  cursor: pointer;

  ${WINDOW_DIMENSIONS.TABLET_MEDIA_QUERY} {
    display: none;
  }
`;

const DropdownContainer = styled.div`
  width: 296px;
  background-color: ${COLORS.WHITE};
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  padding: 0 0 16px;
  border-radius: 8px;
  box-shadow: ${STYLES.SHADOW_B};
  z-index: 2000;

  ${WINDOW_DIMENSIONS.TABLET_MEDIA_QUERY} {
    width: 100%;
    max-width: 100%;
  }
`;

const HeaderSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 12px;
  flex-shrink: 0;
  align-self: stretch;
  border-width: 0 0 1px;
  border-color: ${COLORS.NEUTRAL_200};
  border-style: solid;
`;

const UserInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const NameContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const SuperAdminTagContainer = styled.span`
  display: inline-block;
  position: relative;
  left: 4px;
  top: 2px;
  width: 20px;
  pointer-events: none;
`;

const ProfileTags = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 8px;
`;

const MenuSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0 12px;
`;

const MenuItem = styled.a`
  width: 272px;
  height: 44px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  flex-shrink: 0;
  border-radius: 4px;
  border: none;
  background: none;
  cursor: pointer;
  ${FONTS.REGULAR_2}
  color: ${COLORS.NEUTRAL_800};
  text-align: left;
  line-height: 24px;
  flex-grow: 1;
  text-decoration: none;

  &:link,
  &:visited,
  &:active {
    color: ${COLORS.NEUTRAL_800};
    text-decoration: none;
  }

  &:hover {
    background-color: ${COLORS.NEUTRAL_50};
    color: ${COLORS.NEUTRAL_800};
    text-decoration: none;
  }

  ${WINDOW_DIMENSIONS.TABLET_MEDIA_QUERY} {
    width: 100%;
  }
`;
